import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import api from '@/lib/api';

/**
 * The storefront's "not found" page. A store moved here from another platform
 * still has links to its old addresses all over the web, so before saying
 * "not found" the address is looked up and, when it names a product, category
 * or page that came over, the visitor is sent there.
 *
 * Every address is looked up, not only ones that look foreign: Shopify's
 * /products/<handle> and BigCommerce's /<slug>/ look like any other path. The
 * lookup is one cached GET, and only on a page that would otherwise be a 404.
 */
export default function NotFound() {
  const { t } = useTranslation('pages');
  const { pathname, search } = useLocation();
  const address = pathname + search;
  const [answer, setAnswer] = useState<{ address: string; target: string | null } | null>(null);

  useEffect(() => {
    let current = true;
    api
      .get<{ target: string }>(`/redirects/resolve?path=${encodeURIComponent(address)}`)
      .then((res) => current && setAnswer({ address, target: res.target }))
      .catch(() => current && setAnswer({ address, target: null }));
    return () => {
      current = false;
    };
  }, [address]);

  const settled = answer?.address === address ? answer : null;
  if (!settled) return <main className="min-h-[50vh]" aria-busy="true" />;
  // A target equal to this address would just land here again.
  if (settled?.target && settled.target !== address) return <Navigate to={settled.target} replace />;

  return (
    <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 text-center">
      <FileText className="mx-auto h-16 w-16 text-stone-300 dark:text-stone-600" aria-hidden="true" />
      <h1 className="mt-4 text-2xl font-bold text-ink">{t('notFound.heading')}</h1>
      <p className="mt-2 text-ink-muted">{t('notFound.description')}</p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center justify-center rounded-lg bg-sage-600 px-4 py-2 text-sm font-medium text-white hover:bg-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
      >
        {t('notFound.goHome')}
      </Link>
    </main>
  );
}
